import { Box, Button, Typography } from "@mui/material";
import { motion, AnimatePresence } from "motion/react";
import { Zap } from "lucide-react";
import { ChangeEvent, useState } from "react";
import { palette } from "../theme";

interface BulkValidationBarProps {
  total: number;
  onBulkValidate: (count: number) => void;
}

const MIN_COUNT = 1;
const MAX_COUNT = 999;

export function BulkValidationBar({ total, onBulkValidate }: BulkValidationBarProps): JSX.Element | null {
  const [count, setCount] = useState<number>(30);

  if (total <= 5) return null;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const n = parseInt(e.target.value, 10);
    if (Number.isFinite(n)) setCount(Math.max(MIN_COUNT, Math.min(MAX_COUNT, n)));
  };

  const effective = Math.min(count, total);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.22, ease: [0.2, 0, 0.2, 1] }}
        style={{
          position: "sticky",
          bottom: 16,
          zIndex: 5,
          marginTop: 24,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2.5,
            py: 1.5,
            mx: "auto",
            backgroundColor: palette.ink,
            color: palette.surface,
            borderRadius: 999,
            boxShadow: "0 8px 32px -12px rgba(26, 26, 26, 0.45)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="overline"
              sx={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.18em" }}
            >
              Validation en masse
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="input"
                type="number"
                value={count}
                onChange={onChange}
                aria-label="Số lượng"
                min={MIN_COUNT}
                max={MAX_COUNT}
                sx={{
                  width: 64,
                  px: 1,
                  py: 0.5,
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 6,
                  color: palette.surface,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: "0.85rem",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  outline: "none",
                  "&:focus": { borderColor: palette.saffron },
                  "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.65)", fontFamily: '"Geist", system-ui, sans-serif' }}
              >
                bình luận tiếp theo
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={() => onBulkValidate(effective)}
            startIcon={<Zap size={15} strokeWidth={1.8} />}
            sx={{
              backgroundColor: palette.saffron,
              color: palette.ink,
              "&:hover": { backgroundColor: "#d6a630" },
            }}
          >
            Xác nhận {effective}
          </Button>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
}
